export const JST_TIME_ZONE = "Asia/Tokyo" as const;
export const STALE_START_FALLBACK_MS = 5 * 60_000;

export interface TimeEndpointResponse {
  readonly ok: boolean;
  readonly status?: number;
  json(): Promise<unknown>;
}

export interface TimeRequestInit {
  readonly method: "GET";
  readonly cache: "no-store";
  readonly headers: Readonly<Record<string, string>>;
  readonly signal: AbortSignal;
}

export type TimeFetcher = (
  url: string,
  init: TimeRequestInit,
) => Promise<TimeEndpointResponse>;

export interface SynchronizedClockOptions {
  readonly endpoint?: string;
  readonly fetcher?: TimeFetcher;
  readonly monotonicNow?: () => number;
  readonly wallNow?: () => number;
  readonly resyncIntervalMs?: number;
  /** Maximum time for one fetch-and-JSON sample before it is abandoned. */
  readonly requestTimeoutMs?: number;
}

export interface SynchronizeOptions {
  /** Multiple samples reduce the effect of transient network congestion. */
  readonly sampleCount?: number;
}

export interface ClockSyncResult {
  readonly source: "server";
  readonly roundTripTimeMs: number;
  readonly synchronizedAtEpochMs: number;
  readonly offsetFromDeviceClockMs: number;
  readonly samplesAttempted: number;
  readonly samplesSucceeded: number;
}

export interface ClockStatus {
  readonly source: "device" | "server";
  readonly synchronized: boolean;
  readonly roundTripTimeMs: number | null;
  readonly lastSynchronizedAtEpochMs: number | null;
  readonly resyncIntervalMs: number;
}

interface TimeSample {
  readonly serverEpochMs: number;
  readonly startedMonotonicMs: number;
  readonly endedMonotonicMs: number;
  readonly midpointMonotonicMs: number;
  readonly roundTripTimeMs: number;
}

const DEFAULT_RESYNC_INTERVAL_MS = 60_000;
const DEFAULT_REQUEST_TIMEOUT_MS = 4_000;
const DEFAULT_SAMPLE_COUNT = 3;

function defaultMonotonicNow(): number {
  if (typeof globalThis.performance?.now === "function") {
    return globalThis.performance.now();
  }

  return Date.now();
}

function defaultWallNow(): number {
  return Date.now();
}

const defaultFetcher: TimeFetcher = async (url, init) => {
  if (typeof globalThis.fetch !== "function") {
    throw new Error("fetch is unavailable; provide a TimeFetcher");
  }

  return globalThis.fetch(url, init);
};

function assertFinite(value: number, name: string): void {
  if (!Number.isFinite(value)) {
    throw new RangeError(`${name} must be finite`);
  }
}

function readStringEpoch(value: unknown): number | null {
  if (typeof value !== "string") return null;

  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Parses the canonical `{ epochMs }` response, with ISO aliases for safety. */
export function parseTimePayload(payload: unknown): number {
  if (typeof payload !== "object" || payload === null) {
    throw new TypeError("time endpoint payload must be an object");
  }

  const record = payload as Record<string, unknown>;
  const numericCandidates = [record.epochMs, record.nowMs, record.timestamp];

  for (const candidate of numericCandidates) {
    if (typeof candidate === "number" && Number.isFinite(candidate)) {
      return candidate;
    }
  }

  const stringCandidates = [record.iso, record.now, record.timestamp];
  for (const candidate of stringCandidates) {
    const parsed = readStringEpoch(candidate);
    if (parsed !== null) return parsed;
  }

  throw new TypeError("time endpoint payload does not contain a valid epochMs");
}

/**
 * A wall clock anchored to monotonic performance time. Device time is used only
 * as a fallback until `/api/time` succeeds. Server samples are placed at the
 * RTT midpoint and the lowest-latency sample wins.
 */
export class SynchronizedClock {
  private readonly endpoint: string;
  private readonly fetcher: TimeFetcher;
  private readonly monotonicNow: () => number;
  private readonly resyncIntervalMs: number;
  private readonly requestTimeoutMs: number;
  private readonly deviceAnchorEpochMs: number;
  private readonly deviceAnchorMonotonicMs: number;

  private anchorEpochMs: number;
  private anchorMonotonicMs: number;
  private lastReturnedEpochMs: number;
  private lastSyncCompletedMonotonicMs: number | null = null;
  private lastSyncResult: ClockSyncResult | null = null;
  private syncInFlight: Promise<ClockSyncResult> | null = null;

  constructor(options: SynchronizedClockOptions = {}) {
    this.endpoint = options.endpoint ?? "/api/time";
    this.fetcher = options.fetcher ?? defaultFetcher;
    this.monotonicNow = options.monotonicNow ?? defaultMonotonicNow;
    this.resyncIntervalMs =
      options.resyncIntervalMs ?? DEFAULT_RESYNC_INTERVAL_MS;
    this.requestTimeoutMs =
      options.requestTimeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;

    if (
      !Number.isFinite(this.resyncIntervalMs) ||
      this.resyncIntervalMs <= 0
    ) {
      throw new RangeError("resyncIntervalMs must be greater than zero");
    }
    if (
      !Number.isFinite(this.requestTimeoutMs) ||
      this.requestTimeoutMs <= 0
    ) {
      throw new RangeError("requestTimeoutMs must be greater than zero");
    }

    const monotonicAtConstruction = this.monotonicNow();
    const wallAtConstruction = (options.wallNow ?? defaultWallNow)();
    assertFinite(monotonicAtConstruction, "monotonicNow()");
    assertFinite(wallAtConstruction, "wallNow()");

    this.deviceAnchorEpochMs = wallAtConstruction;
    this.deviceAnchorMonotonicMs = monotonicAtConstruction;
    this.anchorEpochMs = wallAtConstruction;
    this.anchorMonotonicMs = monotonicAtConstruction;
    this.lastReturnedEpochMs = wallAtConstruction;
  }

  nowEpochMs(): number {
    const monotonic = this.monotonicNow();
    assertFinite(monotonic, "monotonicNow()");
    const candidate =
      this.anchorEpochMs + (monotonic - this.anchorMonotonicMs);

    // Between authoritative samples, monotonic time prevents device-clock
    // edits from moving the display backwards. A completed server sync resets
    // lastReturnedEpochMs below so an inaccurate device clock cannot keep the
    // display frozen ahead of the authoritative time.
    this.lastReturnedEpochMs = Math.max(
      this.lastReturnedEpochMs,
      candidate,
    );
    return this.lastReturnedEpochMs;
  }

  getStatus(): ClockStatus {
    return {
      source: this.lastSyncResult === null ? "device" : "server",
      synchronized: this.lastSyncResult !== null,
      roundTripTimeMs: this.lastSyncResult?.roundTripTimeMs ?? null,
      lastSynchronizedAtEpochMs:
        this.lastSyncResult?.synchronizedAtEpochMs ?? null,
      resyncIntervalMs: this.resyncIntervalMs,
    };
  }

  needsResync(): boolean {
    if (this.lastSyncCompletedMonotonicMs === null) return true;

    const monotonic = this.monotonicNow();
    assertFinite(monotonic, "monotonicNow()");
    return (
      monotonic - this.lastSyncCompletedMonotonicMs >=
      this.resyncIntervalMs
    );
  }

  synchronize(options: SynchronizeOptions = {}): Promise<ClockSyncResult> {
    if (this.syncInFlight !== null) return this.syncInFlight;

    const synchronization = this.performSynchronization(options).finally(() => {
      this.syncInFlight = null;
    });
    this.syncInFlight = synchronization;
    return synchronization;
  }

  async resyncIfDue(
    options: SynchronizeOptions = {},
  ): Promise<ClockSyncResult | null> {
    if (!this.needsResync()) return null;
    return this.synchronize(options);
  }

  /** Force a fresh sample after visibility changes or device wake. */
  resyncAfterResume(
    options: SynchronizeOptions = {},
  ): Promise<ClockSyncResult> {
    return this.synchronize(options);
  }

  private async performSynchronization(
    options: SynchronizeOptions,
  ): Promise<ClockSyncResult> {
    const sampleCount = options.sampleCount ?? DEFAULT_SAMPLE_COUNT;
    if (!Number.isInteger(sampleCount) || sampleCount < 1 || sampleCount > 10) {
      throw new RangeError("sampleCount must be an integer from 1 to 10");
    }

    const samples: TimeSample[] = [];
    const failures: unknown[] = [];
    let syncCompletedMonotonicMs: number | null = null;

    for (let index = 0; index < sampleCount; index += 1) {
      const startedMonotonicMs = this.monotonicNow();
      assertFinite(startedMonotonicMs, "monotonicNow()");
      const controller = new AbortController();
      let timeoutId: ReturnType<typeof setTimeout> | undefined;

      try {
        const request = (async () => {
          const response = await this.fetcher(this.endpoint, {
            method: "GET",
            cache: "no-store",
            headers: { Accept: "application/json" },
            signal: controller.signal,
          });
          const payload = await response.json();
          return { response, payload };
        });
        const timeout = new Promise<never>((_resolve, reject) => {
          timeoutId = setTimeout(() => {
            controller.abort();
            reject(new Error("time synchronization sample timed out"));
          }, this.requestTimeoutMs);
        });
        const { response, payload } = await Promise.race([request(), timeout]);
        const endedMonotonicMs = this.monotonicNow();
        assertFinite(endedMonotonicMs, "monotonicNow()");
        syncCompletedMonotonicMs = endedMonotonicMs;

        if (!response.ok) {
          throw new Error(
            `time endpoint returned HTTP ${response.status ?? "error"}`,
          );
        }

        if (endedMonotonicMs < startedMonotonicMs) {
          throw new Error("monotonic clock moved backwards during time sync");
        }

        const serverEpochMs = parseTimePayload(payload);
        const roundTripTimeMs = endedMonotonicMs - startedMonotonicMs;
        samples.push({
          serverEpochMs,
          startedMonotonicMs,
          endedMonotonicMs,
          midpointMonotonicMs:
            startedMonotonicMs + roundTripTimeMs / 2,
          roundTripTimeMs,
        });
      } catch (error) {
        failures.push(error);
        // A failed request can still consume monotonic time. Record completion
        // without allowing that secondary read to mask the original error.
        const completion = this.monotonicNow();
        if (Number.isFinite(completion)) syncCompletedMonotonicMs = completion;
      } finally {
        if (timeoutId !== undefined) clearTimeout(timeoutId);
      }
    }

    if (samples.length === 0) {
      const firstFailure = failures[0];
      throw new Error("unable to synchronize with /api/time", {
        cause: firstFailure,
      });
    }

    const bestSample = samples.reduce((best, sample) =>
      sample.roundTripTimeMs < best.roundTripTimeMs ? sample : best,
    );

    this.anchorEpochMs = bestSample.serverEpochMs;
    this.anchorMonotonicMs = bestSample.midpointMonotonicMs;
    this.lastSyncCompletedMonotonicMs =
      syncCompletedMonotonicMs ?? bestSample.endedMonotonicMs;

    const synchronizedAtEpochMs =
      this.anchorEpochMs +
      (this.lastSyncCompletedMonotonicMs - this.anchorMonotonicMs);
    const deviceEpochAtSampleMidpoint =
      this.deviceAnchorEpochMs +
      (bestSample.midpointMonotonicMs - this.deviceAnchorMonotonicMs);

    const result: ClockSyncResult = {
      source: "server",
      roundTripTimeMs: bestSample.roundTripTimeMs,
      synchronizedAtEpochMs,
      offsetFromDeviceClockMs:
        bestSample.serverEpochMs - deviceEpochAtSampleMidpoint,
      samplesAttempted: sampleCount,
      samplesSucceeded: samples.length,
    };
    // Cloudflare time is authoritative. In particular, allow the first sync
    // to correct a device clock that is minutes ahead instead of waiting for
    // real time to catch up to the incorrect fallback value.
    this.lastReturnedEpochMs = synchronizedAtEpochMs;
    this.lastSyncResult = result;
    return result;
  }
}

export interface JstDateTimeParts {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
  readonly second: number;
}

const jstDateTimeFormatter = new Intl.DateTimeFormat("ja-JP", {
  timeZone: JST_TIME_ZONE,
  calendar: "gregory",
  numberingSystem: "latn",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

export function getJstDateTimeParts(epochMs: number): JstDateTimeParts {
  assertFinite(epochMs, "epochMs");
  const values = new Map(
    jstDateTimeFormatter
      .formatToParts(new Date(epochMs))
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, Number(part.value)]),
  );

  const read = (key: keyof JstDateTimeParts): number => {
    const value = values.get(key);
    if (value === undefined || !Number.isFinite(value)) {
      throw new Error(`Intl.DateTimeFormat omitted JST ${key}`);
    }
    return value;
  };

  return {
    year: read("year"),
    month: read("month"),
    day: read("day"),
    hour: read("hour") % 24,
    minute: read("minute"),
    second: read("second"),
  };
}

function twoDigits(value: number): string {
  return String(value).padStart(2, "0");
}

export function formatJstTime(epochMs: number): string {
  const parts = getJstDateTimeParts(epochMs);
  return `${twoDigits(parts.hour)}:${twoDigits(parts.minute)}:${twoDigits(parts.second)}`;
}

export function formatJstDateTimeInput(epochMs: number): string {
  const parts = getJstDateTimeParts(epochMs);
  return `${String(parts.year).padStart(4, "0")}-${twoDigits(parts.month)}-${twoDigits(parts.day)}T${twoDigits(parts.hour)}:${twoDigits(parts.minute)}:${twoDigits(parts.second)}`;
}

/**
 * Keep a saved target only while it is still in the future. Initial settings
 * must never reopen with a countdown that has already completed, so stale
 * values move to five minutes after the authoritative current time.
 */
export function normalizeSavedStartAtJst(
  value: string,
  nowEpochMs: number,
): string {
  assertFinite(nowEpochMs, "nowEpochMs");
  const targetEpochMs = parseJstDateTimeInput(value);
  if (targetEpochMs > nowEpochMs) return value;

  const fallbackEpochMs = Math.ceil(
    (nowEpochMs + STALE_START_FALLBACK_MS) / 1_000,
  ) * 1_000;
  return formatJstDateTimeInput(fallbackEpochMs);
}

/** Parse a datetime-local value as fixed-offset JST (which has no DST). */
export function parseJstDateTimeInput(value: string): number {
  const match =
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(
      value,
    );
  if (match === null) {
    throw new RangeError("JST datetime must use YYYY-MM-DDTHH:mm[:ss]");
  }

  const [, yearText, monthText, dayText, hourText, minuteText, secondText] =
    match;
  const expected: JstDateTimeParts = {
    year: Number(yearText),
    month: Number(monthText),
    day: Number(dayText),
    hour: Number(hourText),
    minute: Number(minuteText),
    second: Number(secondText ?? "0"),
  };

  const epochMs = Date.UTC(
    expected.year,
    expected.month - 1,
    expected.day,
    expected.hour - 9,
    expected.minute,
    expected.second,
  );
  const actual = getJstDateTimeParts(epochMs);

  for (const key of Object.keys(expected) as (keyof JstDateTimeParts)[]) {
    if (expected[key] !== actual[key]) {
      throw new RangeError("JST datetime contains an out-of-range value");
    }
  }

  return epochMs;
}
